document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".initially-hidden").forEach((el) => {
    el.classList.remove("initially-hidden");
  });

  initializeLazyImages();
  initializeThemeMeasurements();
  initializePreloader();
});

// Global lazy image loader with skeleton backgrounds
function initializeLazyImages() {
  const lazyImages = document.querySelectorAll(".lazy-fade");

  if (!lazyImages.length) return;

  lazyImages.forEach((img) => {
    // Add skeleton class to wrapper
    const wrapper = img.closest(".lazy-wrapper");
    if (wrapper) {
      wrapper.classList.add("loading");
    }

    // Check if already loaded
    if (img.complete && img.naturalHeight !== 0) {
      handleImageLoad(img, wrapper);
    } else {
      // Listen for load event
      img.addEventListener("load", function () {
        handleImageLoad(img, wrapper);
      });

      // Handle error
      img.addEventListener("error", function () {
        if (wrapper) {
          wrapper.classList.remove("loading");
          wrapper.classList.add("error");
        }
      });
    }
  });
}

function handleImageLoad(img, wrapper) {
  img.classList.add("loaded");
  if (wrapper) {
    wrapper.classList.remove("loading");
    wrapper.classList.add("loaded");
  }
}

function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

function debounceImmediate(func, wait, immediate) {
  let timeout;
  return function () {
    const context = this,
      args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function () {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

const html = document.documentElement;

function setElementHeight(element, { heightVar, onUpdate }) {
  if (!element) return;

  const height = element.offsetHeight;

  if (heightVar) {
    html.style.setProperty(heightVar, `${height}px`);
  }

  if (typeof onUpdate === "function") {
    onUpdate(height);
  }
}

function initializeAnnouncement() {
  const announcementBar = document.querySelector(".announcement-bar");
  if (!announcementBar) return;

  const updateAnnouncement = () =>
    setElementHeight(announcementBar, {
      heightVar: "--announcement-height",
      onUpdate: (height) => {
        window.announcementHeight = height;
      },
    });

  updateAnnouncement();

  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(updateAnnouncement);
    observer.observe(announcementBar);
  } else {
    const debouncedAnnouncement = debounce(updateAnnouncement, 300);
    window.addEventListener("resize", debouncedAnnouncement, {
      passive: true,
    });
  }
}

function initializeHeaderHeight() {
  // Measure the actual bar height. `.navbar` is `position: fixed`, so the
  // `.section-header` wrapper often has ~0 height — never use that for --header-height.
  const mainHeader =
    document.getElementById("navbar") ||
    document.querySelector(".navbar") ||
    document.getElementById("zeusSiteHeader") ||
    document.getElementById("zeusHeader") ||
    document.querySelector(".main-header");
  if (!mainHeader) return;

  const updateHeader = () =>
    setElementHeight(mainHeader, {
      heightVar: "--header-height",
      onUpdate: (height) => {
        window.headerHeight = height;
      },
    });

  updateHeader();

  const throttledHeaderUpdate = throttle(updateHeader, 16);
  window.addEventListener("scroll", throttledHeaderUpdate, {
    passive: true,
  });

  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(updateHeader);
    observer.observe(mainHeader);
  } else {
    const debouncedHeaderUpdate = debounce(updateHeader, 150);
    window.addEventListener("resize", debouncedHeaderUpdate, {
      passive: true,
    });
  }
}

function initializeThemeMeasurements() {
  initializeAnnouncement();
  initializeHeaderHeight();
}

// Helper: Calculate luminance (brightness) of a color based on rgb values like this ' 255, 255 , 255 '
function getLuminance(colorStr) {
  const [r, g, b] = colorStr.split(",").map(Number);
  return (r * 299 + g * 587 + b * 114) / 1000; // Weighted luminance
}

// ============================================
// Site Preloader
// ============================================
function initializePreloader() {
  const preloader = document.getElementById("preloader");
  if (!preloader) return;

  // Calculate scrollbar width to prevent layout shift
  function getScrollbarWidth() {
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.msOverflowStyle = "scrollbar";
    document.body.appendChild(outer);

    const inner = document.createElement("div");
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  }

  // Set scrollbar width CSS variable
  const scrollbarWidth = getScrollbarWidth();
  document.documentElement.style.setProperty(
    "--scrollbar-width",
    `${scrollbarWidth}px`
  );

  // Add active class to body and html
  document.documentElement.classList.add("preloader-active");
  document.body.classList.add("preloader-active");

  // Track loading progress
  let loadedResources = 0;
  let totalResources = 0;
  const resourcesToLoad = [];

  // Find all images
  function findImages() {
    const images = document.querySelectorAll("img");
    images.forEach((img) => {
      if (img.complete && img.naturalHeight !== 0) {
        // Already loaded
        loadedResources++;
      } else {
        resourcesToLoad.push({
          element: img,
          type: "image",
        });
        img.addEventListener("load", handleResourceLoad, { once: true });
        img.addEventListener("error", handleResourceLoad, { once: true });
      }
      totalResources++;
    });
  }

  // Find all videos
  function findVideos() {
    const videos = document.querySelectorAll("video");
    videos.forEach((video) => {
      if (video.readyState >= 2) {
        // Already loaded enough data
        loadedResources++;
      } else {
        resourcesToLoad.push({
          element: video,
          type: "video",
        });
        video.addEventListener("loadeddata", handleResourceLoad, {
          once: true,
        });
        video.addEventListener("error", handleResourceLoad, { once: true });
        // Also check if video can play
        video.addEventListener("canplay", handleResourceLoad, { once: true });
      }
      totalResources++;
    });
  }

  // Find background images
  function findBackgroundImages() {
    const elements = document.querySelectorAll("*");
    elements.forEach((el) => {
      const bgImage = window.getComputedStyle(el).backgroundImage;
      if (
        bgImage &&
        bgImage !== "none" &&
        bgImage.includes("url(") &&
        !bgImage.includes("data:")
      ) {
        const img = new Image();
        const urlMatch = bgImage.match(/url\(["']?([^"']+)["']?\)/);
        if (urlMatch && urlMatch[1]) {
          resourcesToLoad.push({
            element: img,
            type: "background-image",
          });
          img.src = urlMatch[1];
          img.addEventListener("load", handleResourceLoad, { once: true });
          img.addEventListener("error", handleResourceLoad, { once: true });
          totalResources++;
        }
      }
    });
  }

  // Handle resource load
  function handleResourceLoad() {
    loadedResources++;
    checkIfComplete();
  }

  // Check if all resources are loaded
  function checkIfComplete() {
    // Wait a bit for any remaining resources, then check window load
    if (loadedResources >= totalResources || totalResources === 0) {
      // Wait for window load event as well
      if (document.readyState === "complete") {
        setTimeout(hidePreloader, 300);
      } else {
        window.addEventListener("load", () => {
          setTimeout(hidePreloader, 300);
        });
      }
    }
  }

  // Hide preloader
  function hidePreloader() {
    setTimeout(() => {
      preloader.classList.add("hidden");

      // Remove active classes after transition
      setTimeout(() => {
        document.documentElement.classList.remove("preloader-active");
        document.body.classList.remove("preloader-active");
        preloader.style.display = "none";
      }, 500);
    }, 100);
  }

  // Start loading detection
  findImages();
  findVideos();
  findBackgroundImages();

  // Fallback: Hide preloader after maximum wait time (5 seconds)
  const maxWaitTime = 5000;
  setTimeout(() => {
    if (!preloader.classList.contains("hidden")) {
      hidePreloader();
    }
  }, maxWaitTime);

  // Also check on window load
  if (document.readyState === "complete") {
    setTimeout(() => {
      if (loadedResources >= totalResources || totalResources === 0) {
        hidePreloader();
      }
    }, 500);
  } else {
    window.addEventListener("load", () => {
      setTimeout(() => {
        if (loadedResources >= totalResources || totalResources === 0) {
          hidePreloader();
        } else {
          // Force complete after window load
          setTimeout(hidePreloader, 1000);
        }
      }, 500);
    });
  }
}

// Scroll to Top Button
function initializeScrollToTop() {
  const scrollToTopBtn = document.getElementById("scrollToTop");
  if (!scrollToTopBtn) return;

  const outerElement = scrollToTopBtn.querySelector(".scroll-to-top__outer");
  let lastScrollTop = 0;

  function handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight =
      document.documentElement.scrollHeight -
      document.documentElement.clientHeight;
    const scrollProgress =
      documentHeight > 0 ? (scrollTop / documentHeight) * 360 : 0;

    // Show/hide button based on scroll direction
    if (scrollTop > 100) {
      scrollToTopBtn.classList.add("visible");
    } else {
      scrollToTopBtn.classList.remove("visible");
    }

    // Update progress border fill on outer element
    if (outerElement) {
      outerElement.style.setProperty(
        "--scroll-progress",
        `${scrollProgress}deg`
      );
    }

    lastScrollTop = scrollTop;
  }

  // Throttled scroll handler
  const throttledScroll = throttle(handleScroll, 16); // ~60fps
  window.addEventListener("scroll", throttledScroll, { passive: true });

  // Initial check
  handleScroll();

  // Click handler to scroll to top
  scrollToTopBtn.addEventListener("click", () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  });
}

// Initialize scroll to top on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  initializeScrollToTop();
});
